import React, { useState } from 'react';
import { Button, Dialog, DialogContent, Input } from '@material-tailwind/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Installments = () => {
  const [open, setOpen] = useState(false);
  const [paymentType, setPaymentType] = useState('');
  const [installments, setInstallments] = useState(0);
  const [installmentData, setInstallmentData] = useState([]);
  const [formValues, setFormValues] = useState({
    totalAmount: '',
    notes: '',
    currency: '',
  });
  const [firstInstallmentDate, setFirstInstallmentDate] = useState(new Date());
  const [savedPaymentPlans, setSavedPaymentPlans] = useState([]);

  const handlePaymentTypeChange = (event) => {
    event.stopPropagation();
    setPaymentType(event.target.value);
    if (event.target.value === 'scheduled') {
      setInstallments(1);
      setInstallmentData([{ dueDate: new Date(), amount: '', status: 'unpaid' }]);
    } else {
      setInstallments(0);
      setInstallmentData([]);
    }
  };

  const handleInstallmentsChange = (e) => {
    e.stopPropagation();
    const value = e.target.value;
    setInstallments(value);
    const newData = [];
    for (let i = 0; i < value; i++) {
      newData.push({ dueDate: new Date(), amount: '', status: 'unpaid' });
    }
    setInstallmentData(newData);
  };

  const handleInstallmentDetailChange = (index, field, value, e) => {
    e.stopPropagation();
    const newData = [...installmentData];
    newData[index][field] = value;
    setInstallmentData(newData);
  };

  const handleChange = (e) => {
    e.stopPropagation();
    const { name, value } = e.target;
    setFormValues((prevValues) => ({ ...prevValues, [name]: value }));
  };

  const handleSave = () => {
    const paymentPlan = {
      paymentType,
      installments: installmentData,
      ...formValues,
      firstInstallmentDate,
    };

    // Save logic to create a downloadable JSON file
    const blob = new Blob([JSON.stringify(paymentPlan, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment_plan.json';
    a.click();
    URL.revokeObjectURL(url);

    // Update state to show saved payment plan
    setSavedPaymentPlans((prev) => [...prev, paymentPlan]);
    setOpen(false);
  };

  return (
    <div>
      <div className="p-8 border rounded-l shadow flex justify-between">
        <h1 className='font-medium text-xl items-center text-gray-600 '>Payment Plan</h1>
        <Button onClick={() => setOpen(true)}>Create Payment Plan</Button>

        <Dialog className="max-w-2xl max-h-[80vh] overflow-auto" open={open} onClose={() => setOpen(false)}>
          <DialogContent>
            <h2 className="font-medium text-lg">Create Payment Plan</h2>
            <div className="mt-4">
              <label className="block mb-2">Payment Plan Type:</label>
              <select onChange={handlePaymentTypeChange} value={paymentType} className="border p-2 rounded">
                <option value="">Select...</option>
                <option value="one-time">One Time</option>
                <option value="scheduled">Scheduled Installments</option>
              </select>
            </div>

            {paymentType === 'scheduled' && (
              <div className="mt-4">
                <label className="block mb-2">Number of Installments:</label>
                <Input>
                  <Input.Field
                    type="number"
                    min="1"
                    value={installments}
                    onChange={handleInstallmentsChange}
                    className="mt-2 w-full"
                    placeholder="Input number of installments"
                  />
                </Input>
              </div>
            )}

            {paymentType === 'scheduled' && (
              <table className="mt-4 border">
                <thead>
                  <tr>
                    <th className="border p-2">Installment #</th>
                    <th className="border p-2">Due Date</th>
                    <th className="border p-2">Amount</th>
                    <th className="border p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {installmentData.map((installment, index) => (
                    <tr key={index}>
                      <td className="border p-2">{index + 1}</td>
                      <td className="border p-2">
                        <DatePicker
                          selected={installment.dueDate}
                          onChange={(date) => handleInstallmentDetailChange(index, 'dueDate', date,)}
                          dateFormat="MM/dd/yyyy"
                          className="border p-1 rounded w-full"
                        />
                      </td>
                      <td className="border p-2">
                        <Input>
                          <Input.Field
                            type="number"
                            value={installment.amount}
                            onChange={(e) => handleInstallmentDetailChange(index, 'amount', e.target.value)}
                            className="mt-2 w-full"
                            placeholder="Input amount"
                          />
                        </Input>
                      </td>
                      <td className="border p-2">
                        <select
                          value={installment.status}
                          onChange={(e) => handleInstallmentDetailChange(index, 'status', e.target.value)}
                          className="border p-2 rounded"
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Total Amount and Notes Fields */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex-1 pr-2">
                <label className="block mb-2">Total Amount:</label>
                <Input>
                  <Input.Field
                    type="number"
                    name="totalAmount"
                    value={formValues.totalAmount}
                    onChange={handleChange}
                    className="mt-2 w-full"
                    placeholder="Input total amount"
                  />
                </Input>
              </div>
              <div className="flex-1 pr-2">
                <label className="block mb-2">Notes:</label>
                <Input>
                  <Input.Field
                    as="textarea"
                    name="notes"
                    value={formValues.notes}
                    onChange={handleChange}
                    className="mt-2 w-full"
                    placeholder="Input notes"
                  />
                </Input>
              </div>
            </div>

            {/* First Installment Date and Currency Fields */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex-1 pr-2">
                <label className="block mb-2">First Installment Date:</label>
                <DatePicker
                  selected={firstInstallmentDate}
                  onChange={(date) => setFirstInstallmentDate(date)}
                  className="border p-2 rounded w-full"
                />
              </div>
              <div className="flex-1 pr-2">
                <label className="block mb-2">Currency:</label>
                <Input>
                  <Input.Field
                    type="text"
                    name="currency"
                    value={formValues.currency}
                    onChange={handleChange}
                    className="mt-2 w-full"
                    placeholder="Input currency"
                  />
                </Input>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={handleSave}>Save Payment Plan</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Display Saved Payment Plans */}
      <div className="p-8 border rounded-l shadow mt-4">
        <h2 className="font-medium text-lg">Saved Payment Plans:</h2>
        {savedPaymentPlans.length === 0 ? (
          <p>No payment plans saved yet.</p>
        ) : (
          <ul>
            {savedPaymentPlans.map((plan, index) => (
              <li key={index} className="mt-2 border p-2 rounded">
                <h3>Payment Plan {index + 1}</h3>
                <p><strong>Type:</strong> {plan.paymentType}</p>
                <p><strong>Total Amount:</strong> {plan.totalAmount} {plan.currency}</p>
                <p><strong>First Installment Date:</strong> {plan.firstInstallmentDate.toLocaleDateString()}</p>
                <p><strong>Notes:</strong> {plan.notes}</p>

                <h4>Installments:</h4>
                <table className="mt-2 border">
                  <thead>
                    <tr>
                      <th className="border p-2">Installment #</th>
                      <th className="border p-2">Due Date</th>
                      <th className="border p-2">Amount</th>
                      <th className="border p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.installments.map((installment, idx) => (
                      <tr key={idx}>
                        <td className="border p-2">{idx + 1}</td>
                        <td className="border p-2">{new Date(installment.dueDate).toLocaleDateString()}</td>
                        <td className="border p-2">{installment.amount}</td>
                        <td className="border p-2">{installment.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Installments;
